import React, { useEffect, useState } from 'react';
import { 
  Phone, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  User,
  MapPin,
  Zap,
  RefreshCw
} from 'lucide-react';
import { useDoctorData } from '../../context/DoctorDataContext';
import { formatTimeWithTimezone, getTimezoneAbbr, formatMeetingTime } from '../../utils/timezone';
import { useToast } from '../common/ToastContainer';
import LoadingOverlay from '../common/LoadingOverlay';
import AcceptRequestModal from './AcceptRequestModal';
import RequestAttachments from './RequestAttachments';
import PatientConsultationHistory from './PatientConsultationHistory';
import { parseISO } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

interface CallRequest {
  id: string;
  patientName: string;
  patientPhone: string;
  patientEmail?: string;
  symptoms: string[];
  age: number;
  gender: 'female' | 'male' | 'other' | 'prefer-not-to-say';
  urgencyLevel: 'routine' | 'urgent' | 'very-urgent';
  preferredCallTime: string;
  createdAt: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  medicalHistory?: string;
  currentMedications?: string;
}

interface MarketplaceDashboardProps {
  doctorId: string;
}

const MarketplaceDashboard: React.FC<MarketplaceDashboardProps> = ({ doctorId }) => {
  const toast = useToast();
  
  // Get data from context
  const { 
    pendingRequests: availableRequests, 
    activeRequests: myActiveRequests,
    doctorTimezone,
    loading, 
    refreshing, 
    error: contextError,
    refresh,
    stats: contextStats
  } = useDoctorData();
  
  const [error, setError] = useState<string | null>(null);
  const [acceptingRequest, setAcceptingRequest] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<CallRequest | null>(null);
  const [showRefreshSuccess, setShowRefreshSuccess] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false); // ✅ NEW: Accept modal state
  
  // Filters (applied to all loaded data)
  const [urgencyFilter, setUrgencyFilter] = useState<string>('all');
  const [genderFilter, setGenderFilter] = useState<string>('all');
  const [ageFilter, setAgeFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [patientSearchText, setPatientSearchText] = useState<string>('');
  const [symptomSearchText, setSymptomSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all'); // 'all', 'acceptable', 'overdue'
  
  // Sorting
  const [sortField, setSortField] = useState<string>('requestedDateTime');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Pagination (for display only, not API)
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Helper to handle auth errors
  const handleAuthError = (response: Response) => {
    if (response.status === 401 || response.status === 403) {
      console.error('Authentication failed');
      toast.error('Session expired', 'Please log in again');
      localStorage.clear();
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      return true;
    }
    return false;
  };

  // Check if request is overdue (meeting time has passed)
  const isRequestOverdue = (request: any): boolean => {
    // Try to get the meeting date/time from various possible fields
    let meetingDateTime: Date | null = null;
    
    // Option 1: requestedDateTime (combined field)
    if (request.requestedDateTime) {
      meetingDateTime = new Date(request.requestedDateTime);
    }
    // Option 2: requestedDate + requestedTime (separate fields)
    else if (request.requestedDate && request.requestedTime) {
      meetingDateTime = new Date(`${request.requestedDate}T${request.requestedTime}`);
    }
    // Option 3: scheduledMeetingDateTime
    else if (request.scheduledMeetingDateTime) {
      meetingDateTime = new Date(request.scheduledMeetingDateTime);
    }
    // Option 4: preferredCallTime
    else if (request.preferredCallTime) {
      meetingDateTime = new Date(request.preferredCallTime);
    }
    
    if (!meetingDateTime || isNaN(meetingDateTime.getTime())) {
      return false;
    }
    
    const now = new Date();
    return meetingDateTime < now;
  };

  // Get time until meeting
  const getTimeUntilMeeting = (request: any): string => {
    // Try to get the meeting date/time from various possible fields
    let meetingDateTime: Date | null = null;
    
    if (request.requestedDateTime) {
      meetingDateTime = new Date(request.requestedDateTime);
    } else if (request.requestedDate && request.requestedTime) {
      meetingDateTime = new Date(`${request.requestedDate}T${request.requestedTime}`);
    } else if (request.scheduledMeetingDateTime) {
      meetingDateTime = new Date(request.scheduledMeetingDateTime);
    } else if (request.preferredCallTime) {
      meetingDateTime = new Date(request.preferredCallTime);
    }
    
    if (!meetingDateTime || isNaN(meetingDateTime.getTime())) {
      return 'Time not specified';
    }
    
    try {
      const now = new Date();
      const diffMs = meetingDateTime.getTime() - now.getTime();
      
      if (diffMs < 0) {
        // Overdue
        const overdueMins = Math.abs(Math.floor(diffMs / (1000 * 60)));
        if (overdueMins < 60) return `${overdueMins}m overdue`;
        const overdueHours = Math.floor(overdueMins / 60);
        if (overdueHours < 24) return `${overdueHours}h overdue`;
        const overdueDays = Math.floor(overdueHours / 24);
        return `${overdueDays}d overdue`;
      }
      
      // Upcoming
      const mins = Math.floor(diffMs / (1000 * 60));
      if (mins < 60) return `in ${mins}m`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `in ${hours}h`;
      const days = Math.floor(hours / 24);
      return `in ${days}d`;
    } catch {
      return 'Time not specified';
    }
  };

  // Format date nicely
  const formatMeetingDate = (dateString: string) => {
    if (!dateString) return 'Not specified';
    
    try {
      const date = new Date(dateString);
      // Format as "Jan 18, 2026"
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  // Handle refresh with success message
  const handleRefresh = async () => {
    await refresh();
    setShowRefreshSuccess(true);
    setTimeout(() => setShowRefreshSuccess(false), 2000);
  };

  // Combine context error with local error
  useEffect(() => {
    if (contextError) {
      setError(contextError);
    }
  }, [contextError]);

  // Filter and sort requests
  const getFilteredAndSortedRequests = () => {
    console.log('🔍 MARKETPLACE DEBUG - Available requests:', availableRequests.length);
    console.log('🔍 First request sample:', availableRequests[0]);
    
    let filtered = availableRequests.filter(request => {
      // Patient name search (case-insensitive)
      if (patientSearchText.trim() !== '') {
        const patientName = (request.patientName || '').toLowerCase();
        const searchLower = patientSearchText.toLowerCase();
        if (!patientName.includes(searchLower)) {
          return false;
        }
      }
      
      // Symptom search (case-insensitive, searches all symptoms)
      if (symptomSearchText.trim() !== '') {
        const searchLower = symptomSearchText.toLowerCase();
        const hasMatchingSymptom = request.symptoms.some(symptom => 
          symptom.toLowerCase().includes(searchLower)
        );
        if (!hasMatchingSymptom) {
          return false;
        }
      }
      
      // Status filter (overdue vs acceptable)
      if (statusFilter !== 'all') {
        const isOverdue = isRequestOverdue(request);
        if (statusFilter === 'overdue' && !isOverdue) return false;
        if (statusFilter === 'acceptable' && isOverdue) return false;
      }
      
      // Urgency filter
      if (urgencyFilter !== 'all' && request.urgencyLevel !== urgencyFilter) {
        return false;
      }
      
      // Gender filter
      if (genderFilter !== 'all' && request.gender !== genderFilter) {
        return false;
      }
      
      // Age filter
      if (ageFilter !== 'all') {
        if (ageFilter === 'under-25' && request.age >= 25) return false;
        if (ageFilter === '25-40' && (request.age < 25 || request.age > 40)) return false;
        if (ageFilter === 'over-40' && request.age <= 40) return false;
      }
      
      // Date filter
      if (dateFilter !== 'all') {
        // Get meeting date using same logic as isRequestOverdue
        let meetingDate: Date | null = null;
        
        if ((request as any).requestedDateTime) {
          meetingDate = new Date((request as any).requestedDateTime);
        } else if ((request as any).requestedDate && (request as any).requestedTime) {
          meetingDate = new Date(`${(request as any).requestedDate}T${(request as any).requestedTime}`);
        } else if ((request as any).scheduledMeetingDateTime) {
          meetingDate = new Date((request as any).scheduledMeetingDateTime);
        } else if ((request as any).preferredCallTime) {
          meetingDate = new Date((request as any).preferredCallTime);
        }
        
        if (!meetingDate || isNaN(meetingDate.getTime())) return false;
        
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        
        const meetingDay = new Date(meetingDate.getFullYear(), meetingDate.getMonth(), meetingDate.getDate());
        
        if (dateFilter === 'today' && meetingDay.getTime() !== today.getTime()) return false;
        if (dateFilter === 'tomorrow' && meetingDay.getTime() !== tomorrow.getTime()) return false;
        if (dateFilter === 'week' && (meetingDay < today || meetingDay >= nextWeek)) return false;
      }
      
      return true;
    });
    
    // Sort
    filtered.sort((a: any, b: any) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'urgency':
          const urgencyOrder = { 'very-urgent': 3, 'urgent': 2, 'routine': 1 };
          aValue = urgencyOrder[a.urgencyLevel as keyof typeof urgencyOrder] || 0;
          bValue = urgencyOrder[b.urgencyLevel as keyof typeof urgencyOrder] || 0;
          break;
        case 'age':
          aValue = a.age || 0;
          bValue = b.age || 0;
          break;
        case 'gender':
          aValue = a.gender || '';
          bValue = b.gender || '';
          break;
        case 'requestedDateTime':
          aValue = new Date(a.requestedDateTime || a.createdAt).getTime();
          bValue = new Date(b.requestedDateTime || b.createdAt).getTime();
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  };

  const filteredAndSortedRequests = getFilteredAndSortedRequests();
  
  // Paginate for display
  const totalPages = Math.ceil(filteredAndSortedRequests.length / ITEMS_PER_PAGE);
  const paginatedRequests = filteredAndSortedRequests.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleAcceptClick = (request: CallRequest) => {
    setSelectedRequest(request);
    setShowAcceptModal(true);
  };

  const handleAcceptSuccess = async () => {
    setShowAcceptModal(false);
    setSelectedRequest(null);
    // Refresh the data to get updated list
    await refresh();
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'very-urgent': return 'urgency-very-urgent';
      case 'urgent': return 'urgency-urgent';
      case 'routine': return 'urgency-routine';
      default: return 'urgency-routine';
    }
  };

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case 'very-urgent': return <AlertTriangle className="w-4 h-4" />;
      case 'urgent': return <Clock className="w-4 h-4" />;
      case 'routine': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-[400px]">
        <LoadingOverlay message="Loading marketplace..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-luma-text">Marketplace Dashboard</h1>
          
          {/* Success message */}
          {showRefreshSuccess && (
            <div className="flex items-center space-x-2 text-green-600 animate-fade-in">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Updated!</span>
            </div>
          )}
        </div>
        <p className="text-gray-600">Pick up any patient request you'd like to handle</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button 
            onClick={() => handleRefresh()}
            className="mt-2 btn-secondary text-sm"
          >
            Retry
          </button>
        </div>
      )}

      {/* Marketplace Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Available Requests</p>
              <p className="text-2xl font-bold text-luma-text">{contextStats.totalPending}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">My Active Requests</p>
              <p className="text-2xl font-bold text-luma-text">{contextStats.totalActive}</p>
            </div>
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
              <Phone className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Completed Today</p>
              <p className="text-2xl font-bold text-luma-text">{contextStats.completedToday}</p>
            </div>
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Total Completed</p>
              <p className="text-2xl font-bold text-luma-text">{contextStats.totalCompleted}</p>
            </div>
            <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Available Requests */}
      <div className="card relative">
        {/* Refreshing overlay */}
        {refreshing && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-xl">
            <div className="flex items-center space-x-2 text-blue-600">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span className="font-medium">Refreshing...</span>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-luma-text">
            Available Patient Requests ({filteredAndSortedRequests.length})
          </h2>
          <div className="flex space-x-2">
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleRefresh();
              }}
              className="btn-secondary text-sm"
              disabled={loading || refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Always show table with filters */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left">
                  <div className="flex flex-col space-y-1">
                    <button
                      onClick={() => handleSort('urgency')}
                      className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                    >
                      Urgency
                      {sortField === 'urgency' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                    <select
                      value={urgencyFilter}
                      onChange={(e) => { setUrgencyFilter(e.target.value); setCurrentPage(1); }}
                      className="text-xs border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-luma-primary"
                    >
                      <option value="all">All</option>
                      <option value="routine">Routine</option>
                      <option value="urgent">Urgent</option>
                      <option value="very-urgent">Very Urgent</option>
                    </select>
                  </div>
                </th>
                <th className="px-4 py-3 text-left">
                  <div className="flex flex-col space-y-1">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient
                    </div>
                    <input
                      type="text"
                      value={patientSearchText}
                      onChange={(e) => { setPatientSearchText(e.target.value); setCurrentPage(1); }}
                      placeholder="Search..."
                      className="text-xs border border-gray-300 rounded px-2 py-0.5 focus:ring-1 focus:ring-luma-primary"
                      />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <div className="flex flex-col space-y-1">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Symptoms
                      </div>
                      <input
                        type="text"
                        value={symptomSearchText}
                        onChange={(e) => { setSymptomSearchText(e.target.value); setCurrentPage(1); }}
                        placeholder="Search..."
                        className="text-xs border border-gray-300 rounded px-2 py-0.5 focus:ring-1 focus:ring-luma-primary"
                      />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => handleSort('requestedDateTime')}
                        className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                      >
                        Requested Meeting
                        {sortField === 'requestedDateTime' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                      <select
                        value={dateFilter}
                        onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }}
                        className="text-xs border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-luma-primary"
                      >
                        <option value="all">All</option>
                        <option value="today">Today</option>
                        <option value="tomorrow">Tomorrow</option>
                        <option value="week">This Week</option>
                      </select>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => handleSort('createdAt')}
                        className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                      >
                        Requested
                        {sortField === 'createdAt' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <div className="flex flex-col space-y-1">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </div>
                      <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        className="text-xs border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-luma-primary"
                      >
                        <option value="all">All</option>
                        <option value="acceptable">Acceptable</option>
                        <option value="overdue">Overdue</option>
                      </select>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRequests.map((request) => (
                  <tr 
                    key={request.id}
                    className="hover:bg-gray-50"
                  >
                    {/* Urgency Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        request.urgencyLevel === 'very-urgent' 
                          ? 'bg-red-100 text-red-800'
                          : request.urgencyLevel === 'urgent'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {getUrgencyIcon(request.urgencyLevel)}
                        <span className="ml-1">
                          {request.urgencyLevel === 'very-urgent' ? 'Very Urgent' : 
                           request.urgencyLevel === 'urgent' ? 'Urgent' : 'Routine'}
                        </span>
                      </span>
                    </td>

                    {/* Patient Column */}
                    <td className="px-4 py-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-gray-900">
                            {request.patientName || 'Anonymous'}
                          </div>
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-mono rounded">
                            #{request.id || (request as any).requestId}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {request.age} • {request.gender}
                        </div>
                      </div>
                    </td>

                    {/* Symptoms Column */}
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {request.symptoms.slice(0, 2).map((symptom, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            {symptom}
                          </span>
                        ))}
                        {request.symptoms.length > 2 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{request.symptoms.length - 2}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Requested Meeting Column */}
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        {(() => {
                          // Extract date from requestedDate (format: "2026-03-08T00:00:00.000Z")
                          let date = (request as any).requestedDate;
                          if (date && date.includes('T')) {
                            date = date.split('T')[0];
                          }
                          
                          // Get time - priority: requestedTime > requestedSpecificTime > requestedTimeRangeStart
                          let timeStr = (request as any).requestedTime || (request as any).requestedSpecificTime || (request as any).requestedTimeRangeStart;
                          
                          // Convert HH:MM:SS format to HH:MM if needed
                          if (timeStr && timeStr.includes(':')) {
                            const parts = timeStr.split(':');
                            timeStr = `${parts[0]}:${parts[1]}`;
                          }
                          
                          if (!date || !timeStr) {
                            return <div className="text-gray-400">Time not specified</div>;
                          }
                          
                          try {
                            // Get patient's timezone
                            const patientTimezone = (request as any).timezone || 'Asia/Yangon';
                            
                            // Create datetime in patient's timezone
                            const patientDateTime = `${date}T${timeStr}:00`;
                            
                            // Parse as patient's timezone and convert to UTC
                            const utcDate = fromZonedTime(patientDateTime, patientTimezone);
                            
                            // Convert UTC to doctor's timezone for display
                            const formattedDate = formatInTimeZone(utcDate, doctorTimezone, 'MMM d');
                            const formattedTime = formatInTimeZone(utcDate, doctorTimezone, 'h:mm a');
                            
                            return (
                              <div>
                                <div className="font-medium text-gray-900">{formattedDate}</div>
                                <div className="text-lg font-bold text-blue-600">{formattedTime}</div>
                                <div className="text-xs text-gray-400">Requested time</div>
                              </div>
                            );
                          } catch (err) {
                            console.error('❌ Error formatting time:', err);
                            return <div className="text-gray-400">Time not specified</div>;
                          }
                        })()}
                      </div>
                    </td>

                    {/* Requested Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {formatTimeAgo(request.createdAt)}
                      </div>
                    </td>

                    {/* Actions Column */}
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex flex-col space-y-2">
                        <button
                          onClick={() => setSelectedRequest(request)}
                          className="inline-flex items-center justify-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-all"
                        >
                          View Details
                        </button>
                        {isRequestOverdue(request) ? (
                          <button
                            disabled
                            className="inline-flex items-center justify-center px-3 py-1.5 bg-gray-300 text-gray-500 rounded-lg text-xs font-medium cursor-not-allowed"
                            title="This consultation time has passed"
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Overdue
                          </button>
                        ) : (
                          <button
                            onClick={() => handleAcceptClick(request)}
                            className="inline-flex items-center justify-center px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-all"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Accept & Choose Time
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                
                {/* Empty state row */}
                {paginatedRequests.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-gray-500 font-medium">
                        {availableRequests.length === 0 
                          ? 'No available requests at the moment' 
                          : 'No requests match your filters'}
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        {availableRequests.length === 0 
                          ? 'New requests will appear here when you refresh'
                          : 'Try adjusting your filters above'}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        
        {/* Pagination Controls */}
        {!loading && filteredAndSortedRequests.length > 0 && (
          <div className="mt-6 space-y-3">
            {/* Showing count */}
            <div className="text-center text-sm text-gray-600">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredAndSortedRequests.length)} of {filteredAndSortedRequests.length} requests
            </div>
            
            {/* Page navigation */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2">
                {/* Previous button */}
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1 || refreshing}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {/* Page numbers */}
                <div className="flex items-center space-x-1">
                  {/* First page */}
                  {currentPage > 3 && (
                    <>
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={refreshing}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        1
                      </button>
                      {currentPage > 4 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                    </>
                  )}
                  
                  {/* Pages around current */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page >= currentPage - 2 && page <= currentPage + 2;
                    })
                    .map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        disabled={refreshing}
                        className={`px-3 py-2 border rounded-lg text-sm font-medium ${
                          page === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  
                  {/* Last page */}
                  {currentPage < totalPages - 2 && (
                    <>
                      {currentPage < totalPages - 3 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={refreshing}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>
                
                {/* Next button */}
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages || refreshing}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* My Active Requests */}
      {myActiveRequests.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-semibold text-luma-text mb-6">My Active Requests</h2>
          <div className="space-y-4">
            {Array.isArray(myActiveRequests) && myActiveRequests.map((request) => (
              <div key={request.id} className="request-card bg-blue-50 border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-luma-text">
                        {request.patientName || 'Anonymous Patient'}
                      </h3>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-mono rounded">
                        #{request.id || (request as any).requestId}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {request.symptoms.join(', ')}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      In Progress
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Patient Details Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-semibold text-luma-text">
                  Patient Details - {selectedRequest.patientName || 'Anonymous'}
                </h3>
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-mono rounded">
                  #{selectedRequest.id || (selectedRequest as any).requestId}
                </span>
              </div>
              <button
                onClick={() => setSelectedRequest(null)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <User className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Urgency */}
              <div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  selectedRequest.urgencyLevel === 'very-urgent' 
                    ? 'bg-red-100 text-red-800'
                    : selectedRequest.urgencyLevel === 'urgent'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {getUrgencyIcon(selectedRequest.urgencyLevel)}
                  <span className="ml-1">
                    {selectedRequest.urgencyLevel === 'very-urgent' ? 'Very Urgent' : 
                     selectedRequest.urgencyLevel === 'urgent' ? 'Urgent' : 'Routine'}
                  </span>
                </span>
              </div>

              {/* Patient Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">👤 Patient Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Age</p>
                    <p className="font-medium">{selectedRequest.age}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Gender</p>
                    <p className="font-medium">{selectedRequest.gender}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Phone</p>
                    <p className="font-medium">{selectedRequest.patientPhone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Preferred Time</p>
                    <p className="font-medium">{selectedRequest.preferredCallTime}</p>
                  </div>
                </div>
              </div>

              {/* Symptoms */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">🩺 Symptoms</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedRequest.symptoms.map((symptom, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {symptom}
                    </span>
                  ))}
                </div>
              </div>

              {/* Medical History */}
              {selectedRequest.medicalHistory && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📋 Medical History</h4>
                  <p className="text-sm text-gray-700">{selectedRequest.medicalHistory}</p>
                </div>
              )}

              {/* Current Medications */}
              {selectedRequest.currentMedications && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">💊 Current Medications</h4>
                  <p className="text-sm text-gray-700">{selectedRequest.currentMedications}</p>
                </div>
              )}

              {/* Patient Attachments for CURRENT Request */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">📎 Attachments for This Consultation</h4>
                <RequestAttachments requestId={selectedRequest.id || (selectedRequest as any).requestId} />
              </div>

              {/* Full Patient History (Previous Consultations + All Files) */}
              {selectedRequest.patientEmail && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">📋 Complete Patient History</h4>
                  <PatientConsultationHistory patientEmail={selectedRequest.patientEmail} />
                </div>
              )}

              {/* Timing */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Requested:</strong> {formatTimeAgo(selectedRequest.createdAt)}
                </p>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setSelectedRequest(null)}
                className="flex-1 btn-secondary"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleAcceptClick(selectedRequest);
                  // Modal will handle the rest
                }}
                className="flex-1 btn-primary"
              >
                <CheckCircle className="w-4 h-4 mr-2 inline" />
                Accept & Choose Time
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accept Request Modal - Choose Exact Time */}
      {showAcceptModal && selectedRequest && (
        <AcceptRequestModal
          request={selectedRequest}
          doctorId={doctorId}
          doctorTimezone={doctorTimezone}
          onClose={() => {
            setShowAcceptModal(false);
            setSelectedRequest(null);
          }}
          onSuccess={handleAcceptSuccess}
        />
      )}
    </div>
  );
};

export default MarketplaceDashboard;

// Note: Accept Request Modal is rendered conditionally above when showAcceptModal is true
